import {
  collection,
  collectionGroup,
  doc,
  addDoc,
  getDocs,
  getDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  setDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

import { db } from "./firebase";

import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";

import { getFunctions, httpsCallable } from "firebase/functions";

/* =========================================================================
 * ESTRUTURA DE DADOS ADOTADA (resumo)
 * =========================================================================
 * escolas/{idEscola}
 * escolas/{idEscola}/professores/{idProfessorDoc}      -> dados do professor/diretor
 * escolas/{idEscola}/turmas/{idTurma}                  -> dados da turma
 * escolas/{idEscola}/turmas/{idTurma}/membros/{uid}    -> vínculo da pessoa com a turma
 *      membros/{uid} = { uid, nome, papeis: ["professor"] | ["aluno"] | ["professor","aluno"] }
 * escolas/{idEscola}/chamadas/{idChamada}
 * escolas/{idEscola}/chamadas/{idChamada}/presencas/{idPessoa}
 * usuarios/{uid}                                        -> coleção GLOBAL (fora de escolas/)
 *      usada para saber, no login, a qual escola e perfil o uid pertence
 *
 * POR QUE "papeis" É UM ARRAY E NÃO UM CAMPO ÚNICO:
 * O mesmo uid pode ser, ao mesmo tempo, PROFESSOR responsável pela turma
 * de professores E ALUNO avaliado nessa mesma turma (é exatamente o caso
 * do diretor/admin da escola). Guardando um array em vez de um campo fixo
 * "papel: professor", conseguimos os dois papéis no MESMO documento
 * membros/{uid}, sem duplicar cadastro.
 * ======================================================================= */

// ---------------------------------------------------------------------------
// CRUD GENÉRICO (sem alterações de lógica — apenas comentado)
// Continua funcionando para qualquer coleção "solta" dentro de uma escola,
// ex.: addItem("avisos", dados, idEscola)
// ---------------------------------------------------------------------------

export async function addItem(collectionName, data, idEscola) {
  try {
    const docRef = await addDoc(collection(db, "escolas", idEscola, collectionName), {
      ...data,
      createdAt: serverTimestamp(),
    });
    console.log("Documento criado com o id", docRef.id);
    return docRef.id;
  } catch (error) {
    console.log("Erro ao criar documento", error);
    throw error;
  }
}

export function getItens(collectionName, callback, idEscola) {
  if (!collectionName) return;
  const q = query(collection(db, "escolas", idEscola, collectionName), orderBy("createdAt", "asc"));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(false);
    } else {
      const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      callback(items);
    }
  });
  return unsubscribe; // chame essa função de retorno para parar de escutar (useEffect cleanup)
}

export async function getDocCollection(collectionName, callback, idEscola) {
  const q = query(collection(db, "escolas", idEscola, collectionName));
  const querySnapshot = await getDocs(q);
  const dados = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  callback(dados);
}

export async function getDocumentoUnico(collectionName, id, callback, idEscola) {
  try {
    const docRef = doc(db, "escolas", idEscola, collectionName, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
      return;
    }
    console.log("Documento não encontrado!");
    return null;
  } catch (error) {
    console.error("Erro ao buscar elemento", error);
    throw error;
  }
}

export async function updateItem(collectionName, docId, data, idEscola) {
  try {
    const docRef = doc(db, "escolas", idEscola, collectionName, docId);
    await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });
  } catch (error) {
    console.error("Erro ao atualizar:", error);
    throw error;
  }
}

export async function deleteItem(collectionName, docId, idEscola) {
  try {
    const docRef = doc(db, "escolas", idEscola, collectionName, docId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Erro ao remover:", error);
    throw error;
  }
}

export async function filtro(collectionName, campo, operador, parametros, callback, idEscola) {
  try {
    const q = query(collection(db, "escolas", idEscola, collectionName), where(campo, operador, parametros));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dados = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      callback(dados);
    });
    return unsubscribe;
  } catch (error) {
    console.error("Erro ao buscar: ", error);
    throw error;
  }
}

export async function atualizarListaDeAlunos(collectionName, listaAlunos, idEscola) {
  console.log(listaAlunos)
  try {
    const batch = writeBatch(db);
    listaAlunos.forEach((aluno) => {
      const { id, ...dados } = aluno;
      const alunoRef = doc(db, "escolas", idEscola, collectionName, id);
      batch.update(alunoRef, { ...dados, dataAtualizacao: new Date() });
    });
    await batch.commit();
    return true;
  } catch (error) {
    console.error("Erro ao atualizar os alunos em lote:", error);
    throw error;
  }
}

export async function deletarVariosRegistros(idEscola, listaDeIdsParaDeletar, collectionName = "aulaTurma") {
  const batch = writeBatch(db);
  try {
    listaDeIdsParaDeletar.forEach((id) => {
      if (!id) return;
      const docRef = doc(db, "escolas", idEscola, collectionName, id);
      batch.delete(docRef);
    });
    await batch.commit();
  } catch (erro) {
    console.error("Erro ao apagar em lote:", erro);
    throw erro;
  }
}

// ---------------------------------------------------------------------------
// AUTENTICAÇÃO / PERFIL — funções auxiliares de baixo nível
// ---------------------------------------------------------------------------

/**
 * ⚠️ NÃO UTILIZADA MAIS: desde que `registrarDiretorCompleto` passou a
 * criar a conta do diretor inteiramente dentro da Cloud Function
 * `criarEscolaAdmin` (Admin SDK, sem passar pelo client SDK do Auth),
 * nada neste arquivo chama `criarAuthUser`. Mantida por enquanto caso
 * algo fora do que revisei ainda dependa dela — candidata a remoção.
 *
 * Cria a conta no Firebase Auth. ATENÇÃO: isso troca a sessão atual do
 * navegador para o usuário recém-criado.
 */
export const criarAuthUser = async (email, senha) => {
  const auth = getAuth();
  const cred = await createUserWithEmailAndPassword(auth, email, senha);
  return cred.user.uid;
};

export const salvarPerfilUsuario = async (uid, dados) => {
  await setDoc(doc(db, "usuarios", uid), {
    ...dados,
    dataCriacao: serverTimestamp(),
  });
};

// ---------------------------------------------------------------------------
// 1) BOOTSTRAP: criar a ESCOLA + o DIRETOR/ADMIN
// ---------------------------------------------------------------------------
/**
 * Fluxo (nenhum usuário está logado ainda, por isso é seguro usar o Auth
 * do client aqui):
 *   1. Cria a conta do diretor no Firebase Auth.
 *   2. Gera os IDs da escola, da turma "Professores" e do doc do professor
 *      ANTES de gravar (padrão já usado no seu código original).
 *   3. Grava tudo em um único batch (atomicidade: ou grava tudo, ou nada).
 *   4. O diretor entra na turma "Professores" com os DOIS papéis ao mesmo
 *      tempo: "professor" (é o responsável pela turma) e "aluno" (também
 *      é avaliado/faz chamada nela) — por isso `papeis: ["professor","aluno"]`.
 */
/**
 * MUDANÇA IMPORTANTE: essa função agora só CHAMA a Cloud Function
 * `criarEscolaAdmin`, que passou a gravar TAMBÉM os documentos do
 * Firestore (não apenas o Auth). O writeBatch que existia aqui foi
 * removido.
 *
 * Motivo: esse batch gravava escola + turma de professores + membro do
 * diretor + usuarios/{uid} tudo de uma vez, do client. Duas coisas
 * quebravam isso:
 *  1. Dentro de um writeBatch (diferente de uma transação), as Security
 *     Rules avaliam get() contra o estado do banco de ANTES do batch —
 *     a regra que confirma "você é o diretor desta escola" não
 *     enxergava a escola sendo criada no MESMO batch, e negava a
 *     escrita da turma.
 *  2. As regras exigiam assinatura ativa para criar turma/professor,
 *     mas o pagamento só acontece depois (Passo 3 do cadastro) — toda
 *     escola nova começa com assinatura inativa.
 * Resultado: a conta era criada no Auth (isso já rodava numa Cloud
 * Function, então não passava por essas regras) mas o batch do
 * Firestore falhava inteiro — sobrava uma conta Auth sem nenhum dado no
 * banco. Mover a gravação para dentro da própria Cloud Function (Admin
 * SDK ignora Security Rules) resolve os dois problemas de uma vez, e a
 * function agora também desfaz (apaga) a conta Auth se o Firestore
 * falhar por qualquer outro motivo — nunca mais fica usuário órfão.
 *
 * ⚠️ CORREÇÃO ADICIONAL (regressão da mudança acima): o Admin SDK cria a
 * conta inteiramente no servidor — diferente do antigo
 * `createUserWithEmailAndPassword` do client, ele NÃO loga o navegador
 * automaticamente. Depois da mudança para a Cloud Function, o diretor
 * ficava sem sessão nenhuma no browser, e o Passo 3 (pagamento) falhava
 * silenciosamente: `createCheckoutSession` exige usuário autenticado,
 * negava a chamada, o `catch` só logava no console (sem avisar o
 * usuário), o Stripe nunca era aberto, e — por consequência — o webhook
 * que atualiza `assinatura.status` nunca era disparado. Os dois sintomas
 * relatados ("não redireciona pro pagamento" e "não atualiza a
 * assinatura no banco") eram, na verdade, o MESMO problema.
 *
 * A correção: depois que a Cloud Function confirma que a conta foi
 * criada, fazemos o login explicitamente aqui no client, com o mesmo
 * e-mail/senha que acabaram de ser cadastrados.
 */
export async function registrarDiretorCompleto(diretorData, email, senha, nomeEscola) {
  const functions = getFunctions();
  const criarEscolaAdmin = httpsCallable(functions, "criarEscolaAdmin");

  const resultado = await criarEscolaAdmin({
    email,
    senha,
    nome: diretorData.nome,
    nomeEscola,
    diretorData
  });
  
  // NOVO: sem isso, o navegador ficava sem sessão até a próxima vez que
  // o usuário fizesse login manualmente — e o Passo 3 (pagamento), que
  // roda logo em seguida na mesma sessão, precisa de autenticação.
  const auth = getAuth();
  await signInWithEmailAndPassword(auth, email, senha);

  return resultado.data; // { uid, idEscola, idTurmaProfessores, idProf }
}


// ---------------------------------------------------------------------------
// 2) CRIAR PROFESSOR (feito pelo diretor, que JÁ está logado)
// ---------------------------------------------------------------------------
/**
 * ATUALIZAÇÃO: a gravação no Firestore (professor, membros nas duas
 * turmas, usuarios/{uid}) foi movida para DENTRO da Cloud Function
 * `criarProfessorAuth` — antes era feita aqui, num writeBatch do client.
 *
 * Motivo: a regra de segurança de criação de `professores` passou a
 * exigir `assinatura.status == "ativo"`, e isso gerava falso-negativo
 * sempre que a assinatura da escola não estivesse ativa naquele momento
 * (inclusive em testes) — a conta era criada no Auth (isso já rodava na
 * Cloud Function) mas o batch do Firestore falhava inteiro, sobrando um
 * usuário órfão. Fazer tudo dentro da Cloud Function (Admin SDK ignora
 * Security Rules) elimina esse problema, e a function agora também
 * desfaz a conta Auth caso o Firestore falhe por outro motivo.
 *
 * Bônus: não é mais necessário descobrir e passar o id da turma de
 * professores manualmente — a function busca isso sozinha em
 * `escolas/{idEscola}.turmaProfessoresId`.
 */
export async function registrarProfessor(professorData, email, senha, idEscola, idTurmaDestino) {
  const functions = getFunctions();
  const criarProfessorAuth = httpsCallable(functions, "criarProfessorAuth");

  const resultado = await criarProfessorAuth({
    email,
    senha,
    nome: professorData.nome,
    idEscola,
    idTurmaDestino,
    data: professorData
  });

  return resultado.data; // { uid, idProf }
}

// ---------------------------------------------------------------------------
// 3) DAR/REMOVER AUTORIDADE EXTRA (ex.: admin também lecionar noutra turma)
// ---------------------------------------------------------------------------
/**
 * Não precisa criar Auth (a pessoa já tem conta) — só adiciona mais um
 * papel ao vínculo dela com a turma alvo. Usa `arrayUnion` porque o
 * documento `membros/{uid}` pode já existir (ex.: já é aluno dessa turma)
 * ou pode não existir ainda — `setDoc(..., {merge:true})` cobre os dois casos.
 */
export async function adicionarPapelNaTurma(idEscola, idTurma, uid, nome, papel) {
  const membroRef = doc(db, "escolas", idEscola, "turmas", idTurma, "membros", uid);
  await setDoc(
    membroRef,
    {
      uid,
      nome,
      papeis: arrayUnion(papel), // "professor" ou "aluno"
      atualizadoEm: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Remove um papel específico do vínculo. Se depois de remover o array
 * "papeis" ficar vazio, apaga o documento inteiro (pessoa deixou de ter
 * qualquer relação com aquela turma).
 */
export async function removerPapelDaTurma(idEscola, idTurma, uid, papel) {
  const membroRef = doc(db, "escolas", idEscola, "turmas", idTurma, "membros", uid);

  await updateDoc(membroRef, { papeis: arrayRemove(papel) });

  const snap = await getDoc(membroRef);
  if (snap.exists() && (snap.data().papeis || []).length === 0) {
    await deleteDoc(membroRef);
  }
}

/**
 * Lista TODOS os vínculos (turmas + papéis) de um usuário em toda a escola.
 * Usa collectionGroup porque "membros" existe dentro de várias turmas
 * diferentes — precisamos varrer todas de uma vez. Por isso cada
 * documento de membro guarda também o campo "uid" (não dá para filtrar
 * por documentId() num collectionGroup do jeito que queremos aqui).
 *
 * Exige o índice: collectionGroup "membros", campo "uid" (Firestore cria
 * a sugestão automaticamente na primeira execução).
 */
export async function buscarMeusVinculos(callback) {
  const functions = getFunctions();
  const buscarVinculosUsuario = httpsCallable(functions, "buscarVinculosUsuario");

  const resultado = await buscarVinculosUsuario();
  const vinculos = resultado.data.vinculos;
  callback(vinculos);
  return vinculos;
}

export async function removerProfessorCompleto(idEscola, idProfessorDoc, uid) {
  const functions = getFunctions();
  const adminDeletarUsuario = httpsCallable(functions, "adminDeletarUsuario");

  await adminDeletarUsuario({ uid, idEscola, idProfessorDoc });
}


export async function buscarProfessores(callback, idEscola,idTurma){
  const q = query(collection(db, "escolas", idEscola,'turmas',idTurma, "membros"), orderBy("createdAt", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        callback(false);
      } else {
        const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        callback(items);
      }
    });
    return unsubscribe; // chame essa função de retorno para parar de escutar (useEffect cleanup)
}

/**
 * Exclui uma turma por completo: a subcoleção `membros` inteira + o
 * documento da turma.
 *
 * CORREÇÃO: `TurmasView.js` fazia só `deleteItem('turmas', id, ...)`.
 * O Firestore NUNCA apaga subcoleções automaticamente junto com o
 * documento pai — a subcoleção `membros` ficava órfã (lixo) no banco,
 * e continuaria sendo contada/lida por qualquer código que faça
 * collectionGroup("membros").
 */
export async function removerTurmaCompleta(idEscola, idTurma) {
  const membrosSnap = await getDocs(collection(db, "escolas", idEscola, "turmas", idTurma, "membros"));

  const batch = writeBatch(db);
  membrosSnap.docs.forEach((m) => batch.delete(m.ref));
  batch.delete(doc(db, "escolas", idEscola, "turmas", idTurma));

  await batch.commit();
}

// ---------------------------------------------------------------------------
// 4) LOGIN
// ---------------------------------------------------------------------------
export async function verificarLogin(email, senha, callback) {
  const auth = getAuth();

  try {
    const credenciais = await signInWithEmailAndPassword(auth, email, senha);
    const uid = credenciais.user.uid;
    const docRef = doc(db, "usuarios", uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() });
      return;
    }
    console.log("Documento não encontrado!");
    return null;
  } catch (error) {
    alert("Verifique email ou senha");
    return error;
  }
}

/**
 * "Esqueci minha senha" — dispara o e-mail de redefinição do próprio
 * Firebase Auth (link temporário, o Firebase cuida de tudo: validação,
 * expiração, tela de nova senha). Não precisa de Cloud Function nem de
 * tocar no Firestore — funciona pra qualquer usuário (admin OU
 * professor), já que ambos são contas do Firebase Auth.
 *
 * Sempre resolve (não lança nem quando o e-mail não existe) de propósito:
 * é a prática recomendada contra enumeração de contas — não dá pra saber,
 * pela resposta, se aquele e-mail está cadastrado ou não.
 */
export async function recuperarSenha(email) {
  const auth = getAuth();
  try {
    await sendPasswordResetEmail(auth, email);
    return {ok: true};
  } catch (error) {
    // auth/invalid-email é o único erro que vale a pena diferenciar pro
    // usuário (o resto — user-not-found, etc. — fica genérico de
    // propósito, ver comentário acima).
    if (error.code === "auth/invalid-email") {
      return {ok: false, erro: "E-mail inválido."};
    }
    console.error("Erro ao enviar e-mail de recuperação:", error);
    return {ok: true};
  }
}